import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import LeadApi from "../api/LeadApi";
import { useLeadData } from "../hooks/useLeadData";
import { Lead } from "../models/LeadModel";
import {
  EDITED_SUCCESS_MSG,
  ERROR_MSG,
  REQUIRED_FIELD,
  SELECT_PRODUCT_CATEGORY_MSG,
  SELECT_PRODUCT_INTERESTED_MSG,
  VALID_EMAIL_MSG,
  VALID_MOBILE_MSG,
} from "../../../../core/data/constants/constant";
import { useStages } from "../../../crmSetting/stages/hooks/useStages";
import { all_routes } from "../../../router/all_routes";
import LoaderComponent from "../../../../core/common/crmComponents/LoaderComponent";
import PageHeader from "../../../../core/common/crmComponents/PageHeader";
import { MODULES } from "../../../../core/data/constants/moduleConstants";
import ConfirmModal from "../components/ConfirmModal";
import {
  getAllUrlParams,
  getNavigationUrl,
} from "../../../../utils/helpers/setAndGetUrlParams";
import useProductOptions from "../../../../utils/hooks/useProductOptions";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { useFunctionalityAccess } from "../../../../core/common/wrapper/HasFunctionalityAccess";
import { setHasNewFilters } from "../../../../core/data/redux/filterSlice";
import {
  updateLeadsListCache,
  updateSingleLeadCache,
} from "./updateLeadsListCache";
import { generateQueryParams } from "../../../../utils/helpers/generateQueryParams";
import {
  setRefetchFlag,
  setReloading,
} from "../../../../core/data/redux/moduleDataSlice";
import { useCachedLeadData } from "../hooks/getCachedLeadData";

const EditLead = () => {
  const route = all_routes;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const location = useLocation();
  const [forceReload, setForceReload] = useState(false);
  const reloading = useSelector((state) => state.moduleData.reloading);

  const urlParams = getAllUrlParams();
  const { filters } = useSelector((state) => state.filter);
  const leadsFilters = filters.leads || [];
  let { queryParams, params } = generateQueryParams(leadsFilters);

  const cachedLeadData = location?.state?.data;
  const { id: leadId } = useParams();
  const { leadData, isLoading, isError, error } = useCachedLeadData(
    leadId,
    cachedLeadData,
    forceReload
  );
  const [isOtherOccupationSelected, setIsOtherOccupationSelected] =
    useState(false);
  const [customOccupation, setCustomOccupation] = useState(
    leadData?.occupation || ""
  );

  const allParams = getAllUrlParams(); // Get all URL params
  const queryString = new URLSearchParams(allParams).toString();

  const leadDetailsUrl = `${route.leadsDetails.replace(
    ":id",
    leadData?._id
  )}?${queryString}`;

  const { stagesData } = useStages({
    ofCompany: leadData?.ofCompany?._id,
    module: MODULES.LEADS,
  });
  const stages = stagesData?.data?.data;
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedStage, setSelectedStage] = useState({});
  const { productCategories, productNamesByCategory } = useProductOptions(
    leadData?.ofCompany?._id
  );
  const hasZoomAccess = useFunctionalityAccess("zoomSetting");

  const mutationUpdate = useMutation({
    mutationFn: ({ id, leadData }) => LeadApi.updateLead(id, leadData),
    onSuccess: (res, { id, leadData }) => {
      if (res.status) {
        toast.success(EDITED_SUCCESS_MSG);
        navigate(leadDetailsUrl);

        updateSingleLeadCache(id, res.data);

        updateLeadsListCache({
          id,
          updatedData: res.data,
          page: urlParams?.page * 1,
          limit: urlParams?.limit * 1,
          queryString: params?.toString(),
          queryParams: queryParams?.toString(),
          companyId: urlParams?.companyid,
        });

        if (res.status && res?.msg) {
          toast.success(res.msg);

          if (res?.msg && res?.data?.leadToDealOn) {
            navigate(route.deals);
            dispatch(setRefetchFlag({ module: "deals", flag: true }));
            queryClient.invalidateQueries(["leads"]);
          }
        }
      }
    },

    onError: (error) => {
      toast.error(error.response?.data?.msg || ERROR_MSG);
    },
  });

  const validationSchema = Yup.object({
    firstName: Yup.string(),
    lastName: Yup.string(),
    email: Yup.string().email(VALID_EMAIL_MSG),
    mobile: Yup.string().matches(
      /^(?:\+91\d{10}|91\d{10}|\d{10})$/,
      "Mobile number must be in one of the formats: +914564566789, 914564566789, or 4564566789."
    ),
    alternateNumber: Yup.string().matches(
      /^(?:\d{10}|\+\d{1,4}\d{10})$/,
      "Mobile number must be either 10 digits or a valid international format (e.g., +914564566789)."
    ),
    description: Yup.string(),
    probability: Yup.string(),
    productCategory: Yup.string(),
    productInterested: Yup.object()
      .shape({
        name: Yup.string().required(SELECT_PRODUCT_INTERESTED_MSG),
        id: Yup.number(),
        _id: Yup.string(),
      })
      .required("Product Name is required"),
    textMessageAboutActivity: Yup.string(),
    meetingStatus: Yup.string(),
    currentStatus: Yup.object().shape({
      name: Yup.string(),
      id: Yup.number(),
      color: Yup.string(),
    }),
    clientCode: Yup.string(),
    address: Yup.object()
      .shape({
        street: Yup.string().nullable(),
        city: Yup.string().nullable(),
        state: Yup.string().nullable(),
        zipcode: Yup.string().nullable(),
      })
      .nullable(),
    occupation: Yup.string().required("Occupation is required"),
    language: Yup.string().required("Language is required"),

    businessType: Yup.string(),
    city: Yup.string().required("City is required"),
  });

  const formik = useFormik({
    initialValues:
      leadData && leadData?._id
        ? {
            ...leadData,
            address:
              typeof leadData?.address === "string"
                ? { street: "", city: leadData.address, state: "", zipcode: "" }
                : leadData?.address || {
                    street: "",
                    city: "",
                    state: "",
                    zipcode: "",
                  },
            firstName: leadData?.firstName || "",
            lastName: leadData?.lastName || "",
            email: leadData?.email || "",
            mobile: leadData?.mobile || "",
            probability: leadData?.probability || "",
            productCategory: leadData.productCategory || "",
            productInterested: leadData?.productInterested || {
              name: "",
              id: null,
              _id: "",
            },
            description: leadData?.description || "",
            currentStatus:
              leadData?.currentStatus &&
              typeof leadData?.currentStatus === "object"
                ? leadData.currentStatus
                : { name: "", color: "", id: null },
            clientCode: leadData?.clientCode || "",
            occupation: leadData?.occupation || "",
            language: leadData?.language || "",
            businessType: leadData?.businessType || "B2C",
            alternateNumber: leadData?.alternateNumber || "",
            city: leadData?.city || "",
            meetingStatus: leadData?.meetingStatus || "",
          }
        : new Lead(),
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const updatedFields = {};
      const messages = [];

      // Compare values with original leadData
      for (const key in validationSchema.fields) {
        if (values[key] !== undefined && formik.dirty) {
          // Handle `currentStatus` as a special case (object comparison)
          if (key === "currentStatus") {
            const oldValue = leadData[key]?.name || "N/A";
            const newValue = values[key]?.name || "N/A";

            if (oldValue !== newValue) {
              updatedFields[key] = values[key];
              messages.push(
                `<b> ${MODULES.LEADS.slice(
                  0,
                  -1
                )}  ${key}</b> updated from <b>${oldValue}</b> to <b>${newValue}</b>`
              );
            }
          } else if (key === "address") {
            // Handle address object change
            const oldAddress = leadData[key] || {};
            const newAddress = values[key] || {};

            // Compare individual fields inside address
            for (const addressKey in newAddress) {
              const oldField = oldAddress[addressKey] || "N/A";
              const newField = newAddress[addressKey] || "N/A";

              if (oldField !== newField) {
                updatedFields[key] = newAddress;
                messages.push(
                  `<b>Address - ${addressKey}</b> updated from <b>${oldField}</b> to <b>${newField}</b>`
                );
              }
            }
          } else if (key === "productInterested") {
            // Handle `productInterested` as an object change
            const oldProduct = leadData[key] || {
              name: "N/A",
              id: null,
              _id: "",
            };
            const newProduct = values[key] || {
              name: "N/A",
              id: null,
              _id: "",
            };

            const oldProductName = oldProduct.name || "N/A";
            const newProductName = newProduct.name || "N/A";

            if (oldProductName !== newProductName) {
              updatedFields[key] = values[key];
              messages.push(
                `<b>Product Interested</b> updated from <b>${oldProductName}</b> to <b>${newProductName}</b>`
              );
            }
          } else {
            // Default case for other fields
            const oldValue = leadData[key] || "N/A";
            const newValue = values[key] || "N/A";

            if (oldValue !== newValue) {
              updatedFields[key] = newValue;
              messages.push(
                `<b>${key}</b> updated from <b>${oldValue}</b> to <b>${newValue}</b>`
              );
            }
          }
        }
      }

      // If no updates, show a toast warning
      if (Object.keys(updatedFields).length === 0) {
        toast.warn("No changes were made to the lead data.");
        return; // Stop further execution if no changes
      }

      // Log or save timeline message
      if (messages.length > 0) {
        const timelineMessage = messages.join(", ");

        // Include the timelineMessage in your API call
        mutationUpdate.mutate({
          id: leadId,
          leadData: {
            ...updatedFields,
            textMessageAboutActivity: timelineMessage,
          },
        });
      }
    },
  });

  useEffect(() => {
    if (Object.keys(formik.errors).length > 0) {
      Object.values(formik.errors).forEach((error) => {
        if (typeof error === "object") {
          Object.values(error).forEach((nestedError) => {
            toast.error(nestedError);
          });
        } else {
          toast.error(error);
        }
      });
    }
  }, [formik.submitCount]);

  useEffect(() => {
    if (leadData?.data) {
      formik.setValues({
        ...formik.initialValues,
        ...leadData.data,
      });
    }
  }, [leadData]);

  useEffect(() => {
    dispatch(setHasNewFilters());
  }, []);

  useEffect(() => {
    if (reloading) {
      setForceReload(true);
      queryClient.invalidateQueries(["lead", leadId]);
      dispatch(setReloading(false));
    }
  }, [reloading, setForceReload]);

  useEffect(() => {
    if (forceReload) {
      setForceReload(false);
    }
  }, [forceReload]);

  const statusChangeHandler = (e) => {
    const selectedOption = JSON.parse(e.target.value);
    setSelectedStage(selectedOption);
    formik.setFieldValue("currentStatus", selectedOption);
    if (selectedOption?.toDeal) {
      setShowConfirmModal(true);
    }
  };

  const handleConfirm = () => {
    setShowConfirmModal(false);
  };

  return (
    <>
      {isLoading ? (
        <LoaderComponent />
      ) : (
        <>
          <div className="page-wrapper">
            <div className="content">
              <div className="row">
                <div className="col-md-12">
                  {/* Page Header */}
                  <PageHeader pageName="Edit Lead" moduleName="lead" />
                  {/* /Page Header */}
                </div>
              </div>
              <div className="contact-head">
                <div className="row align-items-center">
                  <div className="col-sm-6">
                    <ul className="contact-breadcrumb">
                      <li>
                        <div
                          onClick={() =>
                            navigate(getNavigationUrl(route.leads))
                          }
                          className="navigate-link"
                        >
                          <i className="ti ti-arrow-narrow-left text-capitalize me-1" />
                          Leads
                        </div>
                      </li>
                      <li className="text-capitalize">
                        <div
                          onClick={() => navigate(leadDetailsUrl)}
                          className="navigate-link"
                        >
                          {leadData?.firstName || "N/A"}{" "}
                          {leadData?.lastName || "N/A"}
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="row mb-3">
                <form onSubmit={formik?.handleSubmit}>
                  <div className="accordion" id="main_accordion">
                    {/* Basic Info */}
                    <div className="accordion-item rounded mb-3">
                      <div
                        className={`accordion-header ${
                          (formik.errors.firstName &&
                            formik.touched.firstName) ||
                          (formik.errors.lastName && formik.touched.lastName) ||
                          (formik.errors.email && formik.touched.email) ||
                          (formik.errors.mobile && formik.touched.mobile) ||
                          (formik.errors.city && formik.touched.city)
                            ? "bg-danger"
                            : ""
                        }`}
                      >
                        <Link
                          to="#"
                          className="accordion-button accordion-custom-button bg-white rounded fw-medium text-dark"
                          data-bs-toggle="collapse"
                          data-bs-target="#basic"
                        >
                          <span className="avatar avatar-md rounded text-dark border me-2">
                            <i className="ti ti-user-plus fs-20" />
                          </span>
                          Basic Info
                        </Link>
                      </div>
                      <div
                        className="accordion-collapse collapse show"
                        id="basic"
                        data-bs-parent="#main_accordion"
                      >
                        <div className="accordion-body border-top">
                          <div className="row">
                            {/* First Name */}
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="col-form-label">
                                  First Name{" "}
                                </label>
                                <div className="input-group">
                                  <input
                                    type="text"
                                    className="form-control text-capitalize"
                                    name="firstName"
                                    value={
                                      formik.values.firstName ||
                                      leadData?.firstName
                                    }
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                  />
                                  <div className="input-group-text">
                                    <i className="ti ti-pencil" />
                                  </div>
                                </div>
                                {formik.touched.firstName &&
                                  formik.errors.firstName && (
                                    <div className="text-danger">
                                      {formik.errors.firstName}
                                    </div>
                                  )}
                              </div>
                            </div>

                            {/* Last Name */}
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="col-form-label">
                                  Last Name{" "}
                                </label>
                                <div className="input-group">
                                  <input
                                    type="text"
                                    className="form-control text-capitalize"
                                    name="lastName"
                                    value={
                                      formik.values.lastName ||
                                      leadData?.lastName
                                    }
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                  />
                                  <div className="input-group-text">
                                    <i className="ti ti-pencil" />
                                  </div>
                                </div>
                                {formik.touched.lastName &&
                                  formik.errors.lastName && (
                                    <div className="text-danger">
                                      {formik.errors.lastName}
                                    </div>
                                  )}
                              </div>
                            </div>

                            {/* Email */}
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="col-form-label">Email</label>
                                <div className="input-group">
                                  <input
                                    type="email"
                                    className="form-control"
                                    name="email"
                                    value={
                                      formik.values.email || leadData?.email
                                    }
                                    onChange={(e) =>
                                      formik.setFieldValue(
                                        "email",
                                        e.target.value.trim()
                                      )
                                    }
                                    onBlur={formik.handleBlur}
                                  />
                                  <div className="input-group-text">
                                    <i className="ti ti-pencil" />
                                  </div>
                                </div>
                                {formik.touched.email &&
                                  formik.errors.email && (
                                    <div className="text-danger">
                                      {formik.errors.email}
                                    </div>
                                  )}
                              </div>
                            </div>

                            {/* Phone */}
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="col-form-label">Phone</label>
                                <div className="input-group">
                                  <input
                                    type="text"
                                    className="form-control"
                                    name="mobile"
                                    value={
                                      formik.values.mobile || leadData?.mobile
                                    }
                                    onChange={(e) =>
                                      formik.setFieldValue(
                                        "mobile",
                                        e.target.value.trim()
                                      )
                                    }
                                    onBlur={formik.handleBlur}
                                  />
                                  <div className="input-group-text">
                                    <i className="ti ti-pencil" />
                                  </div>
                                </div>
                                {formik.touched.mobile &&
                                  formik.errors.mobile && (
                                    <div className="text-danger">
                                      {formik.errors.mobile}
                                    </div>
                                  )}
                              </div>
                            </div>

                            {/* probability */}
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="col-form-label">
                                  Probability
                                </label>
                                <div className="input-group">
                                  <select
                                    name="probability"
                                    className="form-select"
                                    value={
                                      formik.values.probability ||
                                      leadData?.probability
                                    }
                                    onChange={(e) => {
                                      formik.setFieldValue(
                                        "probability",
                                        e.target.value
                                      );
                                    }}
                                  >
                                    <option value="">Select Probability</option>
                                    <option value="high">High</option>
                                    <option value="medium">Medium</option>
                                    <option value="low">Low</option>
                                  </select>
                                  <div className="input-group-text">
                                    <i className="ti ti-pencil" />
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* alternateNumber */}
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="col-form-label">
                                  Alternate Phone Number
                                </label>
                                <div className="input-group">
                                  <input
                                    type="text"
                                    name="alternateNumber"
                                    className="form-control"
                                    value={
                                      formik.values.alternateNumber ||
                                      leadData?.alternateNumber
                                    }
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                  />
                                  <div className="input-group-text">
                                    <i className="ti ti-pencil" />
                                  </div>
                                </div>
                                {formik.touched?.alternateNumber &&
                                formik.errors?.alternateNumber ? (
                                  <div className="text-danger">
                                    {formik.errors?.alternateNumber}
                                  </div>
                                ) : null}
                              </div>
                            </div>

                            {/* Company List */}
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="col-form-label">
                                  Company Name
                                </label>
                                <div className="input-group">
                                  <input
                                    className="form-control"
                                    name="ofCompany"
                                    value={leadData?.ofCompany?.name || ""}
                                    disabled
                                  />
                                  <div className="input-group-text">
                                    <i className="ti ti-lock" />
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* current status */}
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="col-form-label">Status</label>
                                <div className="input-group">
                                  <select
                                    name="currentStatus"
                                    className="form-select text-capitalize"
                                    value={
                                      JSON.stringify(
                                        formik?.values?.currentStatus
                                      ) || ""
                                    } // Ensure the default value matches the initial state
                                    onChange={(e) => statusChangeHandler(e)}
                                    onBlur={() =>
                                      formik?.setFieldTouched(
                                        "currentStatus",
                                        true
                                      )
                                    }
                                  >
                                    <option value="">Select a stage</option>
                                    {stages?.map((stage) => (
                                      <option
                                        className="text-capitalize"
                                        key={stage?.id}
                                        value={JSON.stringify(stage)} // Pass the entire stage object
                                      >
                                        {stage?.name}
                                      </option>
                                    ))}
                                  </select>
                                  <div className="input-group-text">
                                    <i className="ti ti-pencil" />
                                  </div>
                                </div>

                                {formik?.touched?.currentStatus &&
                                formik?.errors?.currentStatus ? (
                                  <div className="text-danger">
                                    {formik?.errors?.currentStatus}
                                  </div>
                                ) : null}
                              </div>
                            </div>

                            {/* Product Category Select Box */}
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="col-form-label">
                                  Product Category
                                </label>
                                <Select
                                  className="select2"
                                  classNamePrefix="react-select"
                                  options={productCategories} // Options for product categories
                                  placeholder="Choose a product category"
                                  name="productCategory"
                                  value={
                                    productCategories?.find(
                                      (option) =>
                                        option.label ===
                                        formik.values.productCategory
                                    ) || null
                                  }
                                  onChange={(selectedOption) => {
                                    formik.setFieldValue(
                                      "productCategory",
                                      selectedOption?.label
                                    );
                                    formik.setFieldValue(
                                      "productInterested",
                                      null
                                    ); // Reset productInterested on category change
                                  }}
                                  onBlur={() =>
                                    formik.setFieldTouched(
                                      "productCategory",
                                      true
                                    )
                                  }
                                />
                                {formik.touched.productCategory &&
                                formik.errors.productCategory ? (
                                  <div className="text-danger">
                                    {formik.errors.productCategory &&
                                      SELECT_PRODUCT_CATEGORY_MSG}
                                  </div>
                                ) : null}
                              </div>
                            </div>

                            {/* Product Name Select Box */}
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="col-form-label">
                                  Product Name
                                  <span className="text-danger">*</span>
                                </label>
                                <Select
                                  className="select2"
                                  classNamePrefix="react-select"
                                  options={
                                    (productNamesByCategory &&
                                    formik?.values?.productCategory
                                      ? productNamesByCategory[
                                          formik.values.productCategory
                                        ]
                                      : []) || []
                                  }
                                  placeholder="Choose a product name"
                                  name="productInterested"
                                  value={
                                    formik.values.productInterested || null
                                  } // Pass the full object to Select
                                  onChange={(selectedOption) =>
                                    formik.setFieldValue(
                                      "productInterested",
                                      selectedOption
                                    )
                                  } // Save the full object on selection
                                  onBlur={() =>
                                    formik.setFieldTouched(
                                      "productInterested",
                                      true
                                    )
                                  }
                                  getOptionLabel={(option) => option.name} // Show product name in dropdown
                                  getOptionValue={(option) => option._id} // Ensure unique value is used internally
                                />

                                {/* Show validation error */}
                                {formik.touched.productInterested &&
                                formik.errors.productInterested ? (
                                  <div className="text-danger">
                                    {formik.errors.productInterested.name}
                                  </div>
                                ) : null}
                              </div>
                            </div>

                            {/* Client code */}
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="col-form-label">
                                  Client Code{" "}
                                </label>
                                <div className="input-group">
                                  <input
                                    type="text"
                                    name="clientCode"
                                    className="form-control"
                                    value={formik.values.clientCode}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                  />
                                  <div className="input-group-text">
                                    <i className="ti ti-pencil" />
                                  </div>
                                </div>
                                {formik.touched?.clientCode &&
                                formik.errors?.clientCode ? (
                                  <div className="text-danger">
                                    {formik.errors?.clientCode}
                                  </div>
                                ) : null}
                              </div>
                            </div>

                            {/* city */}
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="col-form-label">
                                  City<span className="text-danger">*</span>
                                </label>
                                <div className="input-group">
                                  <input
                                    type="text"
                                    name="city"
                                    className="form-control text-capitalize"
                                    value={formik.values.city || leadData?.city}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                  />
                                  <div className="input-group-text">
                                    <i className="ti ti-pencil" />
                                  </div>
                                </div>
                                {formik.touched?.city && formik.errors?.city ? (
                                  <div className="text-danger">
                                    {formik.errors?.city}
                                  </div>
                                ) : null}
                              </div>
                            </div>

                            {/*Zoom Meeting Status */}
                            {hasZoomAccess && (
                              <div className="col-md-6">
                                <div className="mb-3">
                                  <label className="col-form-label">
                                    Zoom Meeting Status
                                  </label>
                                  <div className="input-group">
                                    <select
                                      className="form-select pt-0 rounded-0 fs-6"
                                      value={formik.values.meetingStatus}
                                      onChange={(e) =>
                                        formik.setFieldValue(
                                          "meetingStatus",
                                          e.target.value
                                        )
                                      }
                                      disabled={
                                        formik.values.meetingStatus ===
                                          "attended" || mutationUpdate.isPending
                                      }
                                    >
                                      <option value="registered">
                                        Registered
                                      </option>
                                      <option value="attended">Attended</option>
                                      <option value="not-attended">
                                        Not Attended
                                      </option>
                                    </select>
                                    <div className="input-group-text">
                                      <i
                                        className={`ti ti-${
                                          formik.values.meetingStatus ===
                                          "attended"
                                            ? "lock"
                                            : "pencil"
                                        }`}
                                      ></i>
                                    </div>
                                  </div>

                                  {formik.touched.meetingStatus &&
                                  formik.errors.meetingStatus ? (
                                    <div className="text-danger">
                                      {formik.errors.meetingStatus}
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            )}

                            {/* Preferred Language */}
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="col-form-label">
                                  Preferred Language
                                  <span className="text-danger">*</span>
                                </label>
                                <select
                                  name="language"
                                  className="form-select"
                                  value={formik.values.language}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                >
                                  <option value="">Select Language</option>
                                  <option value="Marathi">Marathi</option>
                                  <option value="Hindi">Hindi</option>
                                  <option value="Engilsh">English</option>
                                </select>
                                {formik.touched.language &&
                                formik.errors.language ? (
                                  <div className="text-danger">
                                    {formik.errors.language}
                                  </div>
                                ) : null}
                              </div>
                            </div>

                            {/* Occupation */}
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="col-form-label">
                                  Occupation{" "}
                                  <span className="text-danger">*</span>
                                </label>
                                <select
                                  name="occupation"
                                  className="form-select text-capitalize"
                                  value={formik.values.occupation}
                                  onChange={(e) => {
                                    const selectedValue = e.target.value;
                                    if (selectedValue === "Other") {
                                      setIsOtherOccupationSelected(true);
                                      formik.setFieldValue("occupation", ""); // Clear value
                                    } else {
                                      setIsOtherOccupationSelected(false);
                                      formik.setFieldValue(
                                        "occupation",
                                        selectedValue
                                      );
                                      setCustomOccupation(""); // Reset custom value
                                    }
                                  }}
                                  onBlur={formik.handleBlur}
                                >
                                  <option
                                    value=""
                                    label={
                                      isOtherOccupationSelected
                                        ? "Enter your occupation in Other Field"
                                        : "Select an occupation"
                                    }
                                  />
                                  {[
                                    "Job",
                                    "Business",
                                    "Student",
                                    "Housewife",
                                    "Retired Person",
                                    "Farmer",
                                    "Other",
                                  ].map((option, index) => (
                                    <option key={index} value={option}>
                                      {option}
                                    </option>
                                  ))}
                                  {customOccupation && (
                                    <option value={customOccupation}>
                                      {customOccupation}
                                    </option>
                                  )}
                                </select>
                                {formik.touched?.occupation &&
                                formik.errors?.occupation ? (
                                  <div className="text-danger">
                                    {formik.errors.occupation}
                                  </div>
                                ) : null}
                              </div>
                            </div>

                            {/* Other Input Field for Occupation */}
                            {isOtherOccupationSelected && (
                              <div className="col-md-6">
                                <div className="mb-3">
                                  <label className="col-form-label">
                                    Other
                                  </label>
                                  <input
                                    type="text"
                                    name="otherOccupation"
                                    className="form-control text-capitalize"
                                    value={customOccupation}
                                    onChange={(e) =>
                                      setCustomOccupation(e.target.value)
                                    }
                                    onBlur={() => {
                                      if (customOccupation.trim()) {
                                        formik.setFieldValue(
                                          "occupation",
                                          customOccupation.trim()
                                        );
                                        setIsOtherOccupationSelected(false);
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        e.preventDefault();
                                        if (customOccupation.trim()) {
                                          formik.setFieldValue(
                                            "occupation",
                                            customOccupation.trim()
                                          );
                                          setIsOtherOccupationSelected(false);
                                        }
                                      }
                                    }}
                                    placeholder="Enter other occupation"
                                  />
                                </div>
                              </div>
                            )}

                            {/* description */}
                            <div className="col-md-12">
                              <div className="mb-0">
                                <label className="col-form-label">
                                  Description
                                </label>
                                <div className="input-group">
                                  <textarea
                                    className="form-control"
                                    name="description"
                                    placeholder="Enter additional details or notes here..."
                                    rows={5}
                                    value={formik.values.description || ""}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                  />
                                  <div className="input-group-text">
                                    <i className="ti ti-pencil" />
                                  </div>
                                </div>
                              </div>
                              {formik.touched.description &&
                              formik.errors.description ? (
                                <div className="text-danger">
                                  {formik.errors.description}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* /Basic Info */}

                    {/* Marketing Attributes */}
                    <div className="accordion-item border-top rounded mb-3">
                      <div className="accordion-header">
                        <Link
                          to="#"
                          className="accordion-button accordion-custom-button rounded bg-white fw-medium text-dark"
                          data-bs-toggle="collapse"
                          data-bs-target="#marketing"
                        >
                          <span className="avatar avatar-md rounded text-dark border me-2">
                            <i className="ti ti-map-pin-cog fs-20" />
                          </span>
                          Marketing Attributes
                        </Link>
                      </div>
                      <div
                        className="accordion-collapse collapse"
                        id="marketing"
                        data-bs-parent="#main_accordion"
                      >
                        <div className="accordion-body border-top">
                          <div className="row">
                            {/* Lead Source */}
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="col-form-label">
                                  Lead Source
                                </label>
                                <div className="input-group">
                                  <input
                                    className="form-control text-capitalize"
                                    name="leadSource"
                                    value={leadData?.leadSource || ""}
                                    disabled
                                  />
                                  <div className="input-group-text">
                                    <i className="ti ti-lock" />
                                  </div>
                                </div>
                                {formik.touched.leadSource &&
                                formik.errors.leadSource ? (
                                  <div className="text-danger">
                                    {formik.errors.leadSource}
                                  </div>
                                ) : null}
                              </div>
                            </div>

                            {/* Business type */}
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="col-form-label">
                                  Business Type
                                </label>
                                <div className="input-group">
                                  <select
                                    name="businessType"
                                    className="form-select"
                                    value={formik.values.businessType}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                  >
                                    <option value="">-None-</option>
                                    <option value="B2C">B2C</option>
                                    <option value="B2B">B2B</option>
                                  </select>
                                  <div className="input-group-text">
                                    <i className="ti ti-pencil" />
                                  </div>
                                </div>
                                {formik.touched.businessType &&
                                formik.errors.businessType ? (
                                  <div className="text-danger">
                                    {formik.errors.businessType}
                                  </div>
                                ) : null}
                              </div>
                            </div>

                            {/* reference source */}
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="col-form-label">
                                  Reference Source{" "}
                                </label>
                                <div className="input-group">
                                  <input
                                    type="text"
                                    name="referenceSource"
                                    className="form-control text-capitalize"
                                    value={formik.values.referenceSource}
                                    onBlur={formik.handleBlur}
                                    disabled
                                  />
                                  <div className="input-group-text">
                                    <i className="ti ti-lock" />
                                  </div>
                                </div>
                                {formik.touched?.referenceSource &&
                                formik.errors?.referenceSource ? (
                                  <div className="text-danger">
                                    {formik.errors?.referenceSource}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* /Marketing Attributes */}

                    {/* Address Info */}
                    <div className="accordion-item border-top rounded mb-3">
                      <div className="accordion-header">
                        <Link
                          to="#"
                          className="accordion-button accordion-custom-button rounded bg-white fw-medium text-dark"
                          data-bs-toggle="collapse"
                          data-bs-target="#address"
                        >
                          <span className="avatar avatar-md rounded text-dark border me-2">
                            <i className="ti ti-map-pin-cog fs-20" />
                          </span>
                          Address Info
                        </Link>
                      </div>
                      <div
                        className="accordion-collapse collapse"
                        id="address"
                        data-bs-parent="#main_accordion"
                      >
                        <div className="accordion-body border-top">
                          <div className="row">
                            <div className="col-md-12">
                              <div className="mb-3">
                                <label className="col-form-label">
                                  Street Address{" "}
                                </label>
                                <div className="input-group">
                                  <input
                                    type="text"
                                    name="address.street"
                                    className="form-control"
                                    value={
                                      formik?.values?.address?.street || ""
                                    }
                                    onChange={formik?.handleChange}
                                    onBlur={formik?.handleBlur}
                                  />
                                  <div className="input-group-text">
                                    <i className="ti ti-pencil" />
                                  </div>
                                </div>
                                {formik?.touched?.address?.street &&
                                formik?.errors?.address?.street ? (
                                  <div className="text-danger">
                                    {formik?.errors?.address?.street}
                                  </div>
                                ) : null}
                              </div>
                            </div>

                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="col-form-label">City </label>
                                <div className="input-group">
                                  <input
                                    type="text"
                                    name="address.city"
                                    className="form-control"
                                    value={
                                      formik?.values?.address?.city ||
                                      leadData?.city ||
                                      ""
                                    }
                                    onChange={formik?.handleChange}
                                    onBlur={formik?.handleBlur}
                                  />
                                  <div className="input-group-text">
                                    <i className="ti ti-pencil" />
                                  </div>
                                </div>
                                {formik?.touched?.address?.city &&
                                formik?.errors?.address?.city ? (
                                  <div className="text-danger">
                                    {formik?.errors?.address?.city || ""}
                                  </div>
                                ) : null}
                              </div>
                            </div>

                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="col-form-label">
                                  State / Province{" "}
                                </label>
                                <div className="input-group">
                                  {" "}
                                  <input
                                    type="text"
                                    name="address.state"
                                    className="form-control"
                                    value={formik?.values?.address?.state || ""}
                                    onChange={formik?.handleChange}
                                    onBlur={formik?.handleBlur}
                                  />
                                  <div className="input-group-text">
                                    <i className="ti ti-pencil" />
                                  </div>
                                </div>
                                {formik?.touched?.address?.state &&
                                formik?.errors?.address?.state ? (
                                  <div className="text-danger">
                                    {formik?.errors?.address?.state}
                                  </div>
                                ) : null}
                              </div>
                            </div>

                            <div className="col-md-6">
                              <div className="mb-0">
                                <label className="col-form-label">
                                  Zipcode{" "}
                                </label>
                                <div className="input-group">
                                  <input
                                    type="text"
                                    name="address.zipcode"
                                    className="form-control"
                                    value={
                                      formik?.values?.address?.zipcode || ""
                                    }
                                    onChange={formik?.handleChange}
                                    onBlur={formik?.handleBlur}
                                  />
                                  <div className="input-group-text">
                                    <i className="ti ti-pencil" />
                                  </div>
                                </div>
                                {formik?.touched?.address?.zipcode &&
                                formik?.errors?.address?.zipcode ? (
                                  <div className="text-danger">
                                    {formik?.errors?.address?.zipcode}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* /Address Info */}
                  </div>
                  <div className="d-flex align-items-center justify-content-end">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={mutationUpdate?.isPending}
                    >
                      {mutationUpdate?.isPending ? "Updating..." : "Update"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Confirmation Modal */}
          <ConfirmModal
            open={showConfirmModal}
            onClose={() => setShowConfirmModal(false)}
            onConfirm={handleConfirm}
            selectedStage={selectedStage}
          />
        </>
      )}
    </>
  );
};

export default EditLead;
